<template>
  <!-- New Card Design -->
  <div :id="'card_' + media.id" :data-test="'card_' + media.id" class="media-card">
    <div v-if="$auth.auth && ($auth.hasRole('super-admin') || (network && network.can && network.can.update))" style="text-align: end; position: relative;">
      <i class="fas fa-pen-to-square edit-icon" :title="$t('edit')" :aria-label="$t('edit')" role="button" tabindex="0" @click="goToEdit(media)"></i>
    </div>
    <article class="card card-hover media-card-section shadow-lg pointer h-100 d-flex flex-column w-100" @click="visitMedia(media)">
      <a class="media-card-thumbnail d-flex align-items-center justify-content-center media-3 rounded-top p-0">
        <MediaThumbnail :media="media" />
      </a>
      <div class="card-body d-flex flex-column">
        <span class="fs-xs fw-bold nav-heading" :class="media.presenters && media.presenters.length > 0 ? 'media-card-header-text-multiline' : 'media-card-header-text-multiline-expanded'">
          <a role="button">{{ media.title }}</a>
        </span>
        <p v-if="media.network && normalizedColumns !== 12" class="card-text">
          <i class="fas fa-user-group"></i>
          {{ media.network.short_name }}
          <span v-if="media.type === 'video' && media.vimeo_error_status" class="badge bg-danger ms-4">
            {{ $t('network.errorUplaodingVimeo') }}
          </span>
          <span v-else-if="media.type === 'video' && !media.vimeo_sync_status" class="badge bg-info ms-4">
            {{ $t('network.uploadInProgress') }}
          </span>
        </p>
      </div>
      <div class="card-footer media-card-footer mt-auto">
        <div class="d-flex">
          <i class="fas fa-calendar-alt text-muted" style="font-size: 0.7rem;"></i>
          <span class="ms-2" style="font-size: 0.6rem;">
            {{ formatDate(media.publish_date || media.creation_date) }}
          </span>
        </div>
        <!--Presenters section-->
        <div v-if="media.presenters && media.presenters.length > 0" class="d-flex align-items-center mt-1 mx-n1">
          <div v-if="media.presenters.length > 1" class="dropdown w-100">
            <div 
              :id="'anchor' + index" 
              ref="anchor"
              class="d-flex align-items-center w-100 dropbtn" 
              @mouseenter="reinitializePopper()"
            >
              <!-- Avatar group for multiple presenters -->
              <div class="d-none d-md-block me-4">
                <div class="avatar-group avatar-group-overlapped">
                  <div class="avatar">
                    <img :src="media.presenters[0].image" :alt="getInitials(media.presenters[0].name)" class="avatar-img rounded-circle" />
                  </div>
                  <div class="avatar d-none d-md-block" style="z-index: 2;">
                    <span class="avatar-text avatar-text-indigo rounded-circle" style="padding-top: 0.1rem; font-size: 0.55rem;">
                      <span class="initial-wrap">
                        <span>+{{ media.presenters.length - 1 }}</span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <!-- Text info for multiple presenters -->
              <div class="flex-grow-1 text-start">
                <p class="presenter-facepile-text-small mb-0">
                  {{ $t('CardFull.presented-by') }}
                  <router-link v-if="media.presenters[0].id" :to="'/users/' + media.presenters[0].id" class="text-decoration-none">
                    {{ media.presenters[0].name }}
                  </router-link>
                   <span v-else>{{ media.presenters[0].name }}</span>
                  <span v-if="media.presenters.length > 1"> {{ $t('and-others', { num: media.presenters.length - 1 }) }}</span>
                </p>
              </div>
            </div>
            <!--Dropdown content for additional presenters-->
            <ul :id="'anchordropdown' + index" ref="anchordropdown" class="list-group dropdown-content">
              <li v-for="(p, i) in media.presenters.slice(1, media.presenters.length)" :key="'presenter-' + i" class="list-group-item d-flex align-items-center">
                <span class="me-2">
                  <div :key="'presenter-' + i" class="avatar">
                    <img :src="p.image" :alt="getInitials(p.name)" class="avatar-img rounded-circle" />
                  </div>
                </span>
                <span class="text-truncate d-inline-block" style="vertical-align: middle;">
                  <router-link v-if="p.id" :to="'/users/' + p.id" class="text-decoration-none">
                    {{ p.name }}
                  </router-link>
                  <span v-else>{{ p.name }}</span>
                </span>
              </li>
            </ul>
          </div>
          <!-- Single presenter layout -->
          <template v-else>
            <div class="d-none d-md-block me-3">
              <div class="avatar-group avatar-group-overlapped">
                <div class="avatar">
                    <img :src="media.presenters[0].image" :alt="getInitials(media.presenters[0].name)" class="avatar-img rounded-circle" />
                </div>
              </div>
            </div>
            <div class="flex-grow-1 text-start">
              <p class="presenter-facepile-text-small mb-0">{{ $t('CardFull.presented-by') }}
                <router-link v-if="media.presenters[0].id" :to="'/users/' + media.presenters[0].id" class="text-decoration-none">
                  {{ media.presenters[0].name }}
                </router-link>
                <span v-else>{{ media.presenters[0].name }}</span>
              </p>
            </div>
          </template>
        </div>
      </div>
    </article>
  </div>
</template>
<script>
import MediaThumbnail from '@/components/commons/Media/Thumbnail.vue';
import { createPopper } from '@popperjs/core';
import moment from 'moment';
import { buildMediaViewUrl } from '@/utils/urlUtils.js';

export default {
  name: 'CardFull',
  components: {
    MediaThumbnail,
  },
  props: {
    media: {
      type: Object,
      default: null,
    },
    columns: {
      type: [Number, Array],
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    network: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      popper: null,
    };
  },
  computed: {
    normalizedColumns() {
      if (Array.isArray(this.columns)) {
        return this.columns.length > 0 ? this.columns[0] : null;
      }
      return this.columns;
    },
  },
  beforeDestroy() {
    if (this.popper && this.popper.destroy) {
      this.popper.destroy();
      this.popper = null;
    }
  },
  mounted() {
    this.initPoppers();
  },
  methods: {
    // Initializes the Popover feature for ALL presenter dropdowns (for certain cases when we exceed >4 presenters)
    initPoppers() {
      if (this.popper && this.popper.destroy) {
        this.popper.destroy();
        this.popper = null;
      }

      let anchorEl = this.$refs.anchor;
      let anchordropdownEl = this.$refs.anchordropdown;

      // initialize poppers
      if (anchorEl && anchordropdownEl) {
        this.popper = createPopper(anchorEl, anchordropdownEl, {
          placement: 'auto',
        });
      }
    },
    reinitializePopper() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.popper) {
            this.popper.update();
          }
        }, 100);
      });
    },
    visitMedia(m) {
      let route;
      if (m.type === 'collection') {
        let networkSlug = this.network && this.network.slug ? '/networks/' + this.network.slug + '/' : '/networks/';
        route = networkSlug + 'collections/' + m.slug;
      } else {
        route = buildMediaViewUrl(m, this.network);
      }
      if (m.permission === 'authorized' && !this.$auth.auth) {
        this.$bus.emit('show-sign-in', route, this.$t('auth.signInMessage.protectedMedia'));
      } else {
        window.open(route, '_blank');
      }
    },
    downloadFile(link) {
      let msg = this.$t('network.downloadFileAlert');
      let action = () => {
        window.open(link, '_blank');
      };
      this.showConfirmPrompt(action, msg);
    },
    formatDate(date) {
      if (!date) return '';
      return moment(date).format('MM-DD-YYYY');
    },
    goToEdit(media) {
      if (media.isCollection) {
        let link = "/networks/collections/" + media.slug + "/edit";
        window.open(link, '_blank');
      } else {
        if (this.network) {
          let link = "/networks/" + this.network.slug + "/media/" + media.slug + "/edit";
          window.open(link, '_blank');
        } else {
          let link = "/media/" + media.slug + "/edit";
          window.open(link, '_blank');
        }
      }
    },
    getInitials(name) {
      if (!name) return '';
      return name.split(' ').filter(n => n).map(n => n[0]).join('.');
    },
  },
};
</script>
<style>
.media-card {
  top: 0px;
  visibility: visible;
  left: 0px;
  opacity: 1;
  transition-duration: 250ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: transform, opacity;
}
.media-card-thumbnail {
  height: 150px;
  text-decoration: none;
}
.media-card-thumbnail .card-img-top {
  height: fit-content;
}
.card-img-top:hover {
  text-decoration: none;
}
.media-card-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.media-card-section .card-footer {
  padding: 0.5rem 1rem;
  border-top: none;
}
.media-card-section .card-body {
  padding: 0.75rem 1rem 0 1rem;
}
.presenter-facepile-text-small {
  font-size: 0.7rem;
  font-weight: 500;
  color: #005f9a;
  line-height: 1.1;
  letter-spacing: inherit;
  font-family: inherit;
  margin-bottom: 0;
}
.media-card-header-text-multiline {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
  height: 2.4em;
}
.media-card-header-text-multiline-expanded {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
  height: 4.8em;
}
/*Avatar*/
.avatar {
  font-size: 1rem;
  position: relative;
  display: inline-block;
}
.avatar .avatar-img {
  width: 100%;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
}
.avatar .avatar-text {
  display: block;
  width: 100%;
  height: 100%;
  position: relative;
  text-transform: uppercase;
  overflow: hidden;
  background: #fff;
  font-weight: 600;
  color: #00acf0;
}
.avatar .avatar-text .initial-wrap {
  display: table;
  width: 100%;
  height: 100%;
}
.avatar .avatar-text .initial-wrap > span {
  position: relative;
  display: table-cell;
  vertical-align: middle;
  text-align: center;
}
.avatar .avatar-text:before {
  content: '';
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  background: rgba(178, 230, 250, 0.5);
}
.avatar .avatar-text.avatar-text-indigo {
  color: #3a55b1;
}
.avatar .avatar-text.avatar-text-indigo:before {
  background: rgba(196, 204, 231, 0.5);
}
.avatar-group {
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
}
.avatar-group .avatar .avatar-img,
.avatar-group .avatar .avatar-text {
  border: 2px solid #fff;
}
.avatar-group.avatar-group-overlapped .avatar {
  margin-right: -13px;
}
.avatar-group.avatar-group-overlapped .avatar:hover {
  z-index: 1;
}
.avatar-group.avatar-group-overlapped .avatar {
  z-index: 1;
}
.avatar-group.avatar-group-overlapped .avatar:first-child {
  z-index: 2;
}
.avatar-group.avatar-group-overlapped .avatar:last-child {
  z-index: 0;
}
.dropbtn {
  padding: 0px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}
.dropbtn:hover {
  padding: 0px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}
.dropdown {
  position: relative;
}
.dropdown-content {
  position: absolute;
  background-color: transparent !important;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 98;
}
.dropdown-down {
  bottom: 100%;
}
.dropdown-up {
  bottom: unset;
}
.dropdown-content a {
  color: #005f9a !important;
  background-color: transparent !important;
  padding: 5px !important;
  text-decoration: none;
  display: block;
}
.dropdown-content a:hover {
  background-color: transparent !important;
}
.dropdown:hover .dropdown-content {
  margin-top: 5%;
}
.cursor-default {
  cursor: default;
}
.min-height-50 {
  min-height: 50px;
}
.media-card-footer {
  background-color: var(--bs-light);
}
.media-card-header-text {
  content: '';
  position: relative;
}
.hovered:hover {
  color: #005f9a !important;
}
@media (min-width: 1570px) {
  .custom-margin-xl-multiple {
    margin-left: -15px;
  }
  .custom-margin-xl-single {
    margin-left: -5px;
  }
}
</style>
<style scoped>
.avatar-group .avatar {
  font-size: 0.6rem !important;
  width: 1.2rem !important;
  height: 1.2rem !important;
}
.dropdown-content .avatar {
  font-size: 0.6rem !important;
  width: 1.4rem !important;
  height: 1.4rem !important;
  flex-shrink: 0;
}
.dropdown-content .list-group-item span {
  font-size: 0.75rem;
}
.edit-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  cursor: pointer;
  color: #212529;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.15);
  padding: 6px;
  border-radius: 50%;
  font-size: 1.1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
</style>